// src/appRWAPromo/components/BotTerminal/KLineChartBot.jsx
import React, { useEffect, useMemo, useRef } from 'react';
import { init, dispose, registerLocale } from 'klinecharts';

registerLocale('en-US', {
  time: 'Time:', open: 'Open:', high: 'High:', low: 'Low:', close: 'Close:', volume: 'Volume:',
  second: 's', minute: 'm', hour: 'h', day: 'd', week: 'w', month: 'mo', year: 'y',
});

/* ---------- helpers ---------- */
const up = (v) => String(v ?? '').trim().toUpperCase();

function normalizeResolution(res) {
  const r = String(res || '60').toLowerCase();
  if (r === '1m') return '1';
  if (r === '3m') return '3';
  if (r === '5m') return '5';
  if (r === '15m') return '15';
  if (r === '30m') return '30';
  if (r === '45m') return '45';
  if (r === '1h') return '60';
  if (r === '2h') return '120';
  if (r === '3h') return '180';
  if (r === '4h') return '240';
  if (r === '1d' || r === 'd') return 'D';
  if (r === '1w' || r === 'w') return 'W';
  if (r === '1mth' || r === '1mo' || r === 'm') return 'M';
  if (/^\d+$/.test(r)) return r;
  return '60';
}
function resToBinanceTf(interval = '60') {
  const r = normalizeResolution(interval);
  return ({
    '1':'1m','3':'3m','5':'5m','15':'15m','30':'30m','45':'1h',
    '60':'1h','120':'2h','180':'3h','240':'4h', D:'1d', W:'1w', M:'1M',
  }[r] || '1h');
}
function parseBase(symbolLike) {
  const raw = up(symbolLike);
  if (!raw) return 'BTC';
  const tail = raw.includes(':') ? raw.split(':').pop() : raw;
  const clean = tail.replace(/[^A-Z0-9]/g, '');
  const m = clean.match(/^([A-Z0-9]+?)(USD|USDT)$/);
  return m ? m[1] : clean;
}

/* ---------- источники ---------- */
async function fetchBinanceKlines({ base, interval = '60', limit = 500 }) {
  const tf = resToBinanceTf(interval);
  const symbol = `${base.toUpperCase()}USDT`;
  const url = `https://api.binance.com/api/v3/klines?symbol=${symbol}&interval=${tf}&limit=${limit}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to load candles: ${res.status}`);
  const arr = await res.json();
  return arr.map(k => ({
    timestamp: k[0], open: +k[1], high: +k[2], low: +k[3], close: +k[4], volume: +k[5],
  }));
}

/* улучшенный WS: безопасное закрытие без консольного спама */
function openBinanceWS({ base, interval = '60' }, onBar) {
  const tf = resToBinanceTf(interval);
  const pair = `${base.toLowerCase()}usdt`;
  const url = `wss://stream.binance.com:9443/ws/${pair}@kline_${tf}`;
  const WSx = window.WebSocket || WebSocket;
  const ws = new WSx(url);

  ws.__closedManually = false;
  ws.__dead = false;
  ws.__safeClose = (code = 1000, reason = 'normal') => {
    if (ws.__closedManually || ws.readyState === WSx.CLOSED) return;
    ws.__closedManually = true;
    ws.onopen = null; ws.onmessage = null; ws.onerror = null; ws.onclose = null;
    try { ws.close(code, reason); } catch {}
  };

  ws.onmessage = (ev) => {
    if (ws.__closedManually || ws.__dead) return;
    try {
      const d = JSON.parse(ev.data); const k = d?.k; if (!k) return;
      onBar({
        timestamp: k.t,
        open:+k.o, high:+k.h, low:+k.l, close:+k.c, volume:+k.v,
        isFinal: !!k.x
      });
    } catch {}
  };
  ws.onerror = () => {}; // подавляем шум
  ws.onclose = () => { ws.__dead = true; };
  return ws;
}

/* ---------- математика и отрисовка ---------- */
function findSwingExtremes(c, k = 2) {
  const highs = [], lows = [];
  for (let i = k; i < c.length - k; i++) {
    const x = c[i]; let isH = true, isL = true;
    for (let j = 1; j <= k; j++) {
      if (!(c[i-j].high < x.high && c[i+j].high < x.high)) isH = false;
      if (!(c[i-j].low  > x.low  && c[i+j].low  > x.low ))  isL = false;
      if (!isH && !isL) break;
    }
    if (isH) highs.push({ idx:i, price:x.high });
    if (isL) lows .push({ idx:i, price:x.low  });
  }
  return { highs, lows };
}
function clusterLevels(points, { relTolPct = 0.15, maxClusters = 12 } = {}) {
  if (!points.length) return [];
  const ps = points.map(p => p.price).sort((a, b) => a - b);
  const clusters = []; let cur = [ps[0]];
  const tol = (v) => (v * relTolPct) / 100;
  for (let i = 1; i < ps.length; i++) {
    const prev = cur[cur.length-1], x = ps[i];
    if (Math.abs(x - prev) <= tol(prev)) cur.push(x);
    else { clusters.push(cur); cur = [x]; }
  }
  clusters.push(cur);
  return clusters
    .map(gr => ({ price: gr.reduce((a,b)=>a+b,0)/gr.length, strength: gr.length }))
    .sort((a,b)=>b.strength - a.strength)
    .slice(0, maxClusters);
}
function linregLines(c, lookback = 120) {
  const L = Math.min(lookback, c.length);
  if (L < 20) return null;
  const s = c.length - L, seg = c.slice(s);
  const lows  = seg.map((v,i)=>({x:s+i,y:v.low}));
  const highs = seg.map((v,i)=>({x:s+i,y:v.high}));
  function fit(arr){
    const n=arr.length, sx=arr.reduce((s,p)=>s+p.x,0), sy=arr.reduce((s,p)=>s+p.y,0);
    const sxx=arr.reduce((s,p)=>s+p.x*p.x,0), sxy=arr.reduce((s,p)=>s+p.x*p.y,0);
    const d=n*sxx - sx*sx; if(!d) return null; const a=(n*sxy - sx*sy)/d, b=(sy - a*sx)/n; return {a,b};
  }
  const lo=fit(lows), hi=fit(highs); if(!lo||!hi) return null;
  const x1=s, x2=s+L-1;
  return {
    low:  { x1, y1: lo.a*x1+lo.b,  x2, y2: lo.a*x2+lo.b },
    high: { x1, y1: hi.a*x1+hi.b, x2, y2: hi.a*x2+hi.b }
  };
}
function donchian(c, n=20){ if(c.length<n) return null; const s=c.slice(-n);
  const hi=Math.max(...s.map(v=>v.high)), lo=Math.min(...s.map(v=>v.low));
  return { hi, lo, mid:(hi+lo)/2 };
}
function srConfigByRes(res) {
  switch (res) {
    case '15':  return { lookbackBars: 240, relTolPct: 0.18, minStrength: 2, maxClusters: 18, focusNear: true,  nearCount: 8,  minSpacingPct: 0.15 };
    case '60':  return { lookbackBars: 520, relTolPct: 0.14, minStrength: 2, maxClusters: 18, focusNear: false, nearCount: 12, minSpacingPct: 0.35 };
    case '240': return { lookbackBars: 520, relTolPct: 0.12, minStrength: 2, maxClusters: 18, focusNear: false, nearCount: 12, minSpacingPct: 0.60 };
    case 'D':   return { lookbackBars: 520, relTolPct: 0.10, minStrength: 3, maxClusters: 18, focusNear: false, nearCount: 14, minSpacingPct: 1.20 };
    default:    return { lookbackBars: 400, relTolPct: 0.14, minStrength: 2, maxClusters: 16, focusNear: false, nearCount: 10, minSpacingPct: 0.35 };
  }
}
function clearOverlays(chart) {
  try { chart.removeOverlay?.({ id: 'ALL' }); } catch {}
  try { chart.removeOverlay?.('ALL'); } catch {}
  try { chart.clearOverlay?.(); } catch {}
}
function drawOverlays(chart, fullCandles, res, opts) {
  const { showDonchian = true, donchianOnD = true } = opts;
  if (!chart || !fullCandles?.length) return;

  clearOverlays(chart);

  const cfg = srConfigByRes(res);
  const candles = fullCandles.slice(-cfg.lookbackBars);

  const swings = findSwingExtremes(candles, 2);
  const clustered = clusterLevels([...swings.highs, ...swings.lows], {
    relTolPct: cfg.relTolPct, maxClusters: cfg.maxClusters
  }).filter(l => l.strength >= cfg.minStrength);

  const px = candles[candles.length-1]?.close ?? fullCandles[fullCandles.length-1]?.close ?? 0;
  const byDist = (a,b)=>Math.abs(a.price-px)-Math.abs(b.price-px);
  const byPower=(a,b)=>b.strength-a.strength;
  let picked = cfg.focusNear ? clustered.sort(byDist).slice(0, cfg.nearCount)
                             : clustered.sort(byPower).slice(0, cfg.nearCount*2);
  const spacing = (cfg.minSpacingPct/100) * (px||1);
  const levels=[];
  for (const l of picked.sort((a,b)=>a.price-b.price)) {
    if (levels.every(x=>Math.abs(x.price-l.price)>=spacing)) levels.push(l);
  }

  levels.forEach((lvl, i) => {
    try {
      chart.createOverlay?.({
        name:'horizontalStraightLine', id:`sr_${res}_${i}`,
        points:[{ value: lvl.price }],
        extendData:{ text: `x${lvl.strength}` },
        styles:{ line:{ color:'rgba(234,179,8,.48)', size:1, style:'dashed' },
                 text:{ color:'rgba(234,179,8,.7)', marginLeft:6, marginTop:-2 } }
      });
    } catch {}
  });

  const lr = linregLines(candles, Math.min(140, candles.length));
  if (lr) {
    const firstIdx = fullCandles.length - candles.length;
    const lastIdx  = fullCandles.length - 1;
    try {
      chart.createOverlay?.({
        name:'straightLine', id:`tl_low_${res}`,
        points:[
          { timestamp: fullCandles[firstIdx].timestamp, value: lr.low.y1 },
          { timestamp: fullCandles[lastIdx].timestamp,  value: lr.low.y2 }
        ],
        styles:{ line:{ color:'rgba(16,185,129,.9)', size:1.5 } }
      });
      chart.createOverlay?.({
        name:'straightLine', id:`tl_high_${res}`,
        points:[
          { timestamp: fullCandles[firstIdx].timestamp, value: lr.high.y1 },
          { timestamp: fullCandles[lastIdx].timestamp,  value: lr.high.y2 }
        ],
        styles:{ line:{ color:'rgba(239,68,68,.9)', size:1.5 } }
      });
    } catch {}
  }

  const allowDon = showDonchian && (donchianOnD || res !== 'D');
  if (allowDon) {
    const dc = donchian(candles, Math.min(20, candles.length));
    if (dc) {
      [['dc_hi', dc.hi,  'rgba(59,130,246,.9)'],
       ['dc_mid',dc.mid, 'rgba(147,197,253,.9)'],
       ['dc_lo', dc.lo,  'rgba(59,130,246,.9)']].forEach(([id, priceV, color]) => {
        try {
          chart.createOverlay?.({
            name:'horizontalStraightLine', id:`${id}_${res}`,
            points:[{ value: priceV }],
            styles:{ line:{ color, size: id==='dc_mid'?1:1.25, style: id==='dc_mid'?'dashed':'solid' } }
          });
        } catch {}
      });
    }
  }
}

/* ---------- компонент ---------- */
export default function KLineChartBot({
  symbol = 'BTC',
  interval = '1h',
  theme = 'dark',
  height = 520,
  locale = 'en-US',
  loadHistory = fetchBinanceKlines,
  openLive = openBinanceWS,
  showDonchian = true,
  donchianOnD = true ,
}) {
  const base = useMemo(() => parseBase(symbol), [symbol]);
  const tvResolution = useMemo(() => normalizeResolution(interval), [interval]);
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const wsRef = useRef(null);
  const cleanupTimerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // 1) закрываем прежний WS
    try { wsRef.current?.__safeClose?.(1000, 'reinit'); } catch {}
    wsRef.current = null;

    // 2) отменяем отложенный dispose и делаем СИНХРОННЫЙ dispose прямо сейчас
    if (cleanupTimerRef.current) {
      clearTimeout(cleanupTimerRef.current);
      cleanupTimerRef.current = null;
    }
    try { dispose(el); } catch {}
    chartRef.current = null;
    el.innerHTML = '';

    const styles = theme === 'dark'
      ? {
          grid:{ show:true,
            horizontal:{ show:true, size:1, color:'rgba(255,255,255,.08)', style:'dashed', dashedValue:[2,2] },
            vertical:{   show:true, size:1, color:'rgba(255,255,255,.08)', style:'dashed', dashedValue:[2,2] },
          },
          candle:{ type:'candle_solid' },
          xAxis:{ tickText:{ color:'#BBB' } },
          yAxis:{ tickText:{ color:'#BBB' } },
          crosshair:{ horizontal:{ line:{ color:'#999' } }, vertical:{ line:{ color:'#999' } } },
        }
      : { candle:{ type:'candle_solid' } };

    let cancelled = false;
    const boot = async () => {
      if (cancelled) return;
      const w = el.clientWidth, h = el.clientHeight;
      if (w === 0 || h === 0) { requestAnimationFrame(boot); return; }

      const chart = init(el, { styles, locale });
      chartRef.current = chart;

      try { chart.setZoomEnabled?.(true); chart.setScrollEnabled?.(true); } catch {}

      try {
        const candles = await loadHistory({ base, interval: tvResolution });
        if (!cancelled) {
          const arr = Array.isArray(candles) ? candles : [];
          chart.applyNewData?.(arr);
          drawOverlays(chart, arr, tvResolution, { showDonchian, donchianOnD });
        }
      } catch {
        if (!cancelled) chart.applyNewData?.([]);
      }

      if (!cancelled) {
        const ws = openLive({ base, interval: tvResolution }, (bar) => {
          const c = chartRef.current; if (!c) return;
          if (ws.__closedManually || ws.__dead) return;
          try {
            c.updateData({
              timestamp: bar.timestamp,
              open:+bar.open, high:+bar.high, low:+bar.low, close:+bar.close,
              volume:+(bar.volume ?? 0),
            });
            if (!c.__upd) c.__upd = 0;
            c.__upd++;
            if (bar.isFinal || c.__upd % 10 === 0) {
              const ds = c.getData?.() || [];
              const last = Array.isArray(ds) ? ds.slice(-520) : [];
              drawOverlays(c, last.length ? last : ds, tvResolution, { showDonchian, donchianOnD });
            }
          } catch {}
        });
        wsRef.current = ws;
      }

      const ro = new ResizeObserver(() => { try { chartRef.current?.resize?.(); } catch {} });
      ro.observe(el);
      el.__ro = ro;

      const onWinResize = () => { try { chartRef.current?.resize?.(); } catch {} };
      window.addEventListener('resize', onWinResize);
      el.__onWinResize = onWinResize;
    };

    boot();

    return () => {
      cancelled = true;

      const el2 = containerRef.current;
      if (el2?.__ro) { try { el2.__ro.disconnect(); } catch {} delete el2.__ro; }
      if (el2?.__onWinResize) { window.removeEventListener('resize', el2.__onWinResize); delete el2.__onWinResize; }

      try { wsRef.current?.__safeClose?.(1000, 'unmount'); } catch {}
      wsRef.current = null;

      // ВАЖНО: задержка dispose ТОЛЬКО в cleanup (исправляет «ping after close»)
      cleanupTimerRef.current = setTimeout(() => {
        try { dispose(el2); } catch {}
        chartRef.current = null;
      }, 10);
    };
  }, [base, theme, height, locale, loadHistory, openLive, tvResolution, showDonchian, donchianOnD]);

  return (
    <div style={{
      width: '100%', height, minHeight: 240,
      background: theme === 'dark' ? '#0D0F14' : '#fff',
      borderRadius: 8, overflow: 'hidden',
      border: theme === 'dark' ? '1px solid rgba(255,255,255,.14)' : '1px solid rgba(0,0,0,.08)',
    }}>
      <div ref={containerRef} style={{ position: 'relative', width: '100%', height: '100%' }} />
    </div>
  );
}
